/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2021 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import * as R from 'ramda';
import { eventChannel } from 'redux-saga';
import { call, cancel, fork, put, select, take, takeLatest } from 'redux-saga/effects';

import { selectLocale, selectOrg, selectToken } from '@nebula/airglow-plugin-unified-shell/dist/selectors';
import Stateful from '@nebula/dorado-stateful-lib';

import { statefulLib } from '../data/prefabs';
import {
  STATEFUL_ERROR,
  STATEFUL_INIT,
  STATEFUL_RESET,
  statefulError,
  statefulInited,
  statefulNavigation,
  statefulPluginSaved
} from '../../common/actions';
import { selectSessionRouteParam } from '../../common/selectors';
import getEnv from '../env';
import doradoReduxStorage, { reset } from '../data/dorado.redux.storage';
import {
  doradoSessionsListQueryError,
  doradoSessionsViewQueryError,
  sessionSaveDuplicateError
} from '../../ui/toast.list';
import { isSessionDuplicate } from '../../common/util/session.utils';

/* eslint-disable no-param-reassign */

export function subscribeHandlers(options) {
  return eventChannel((emit) => {
    options.errorHandler = (type, payload) => {
      emit(statefulError({ type, payload }));
    };
    options.navigationHandler = (path) => {
      emit(statefulNavigation(path));
    };
    options.pluginSaveHandler = (payload) => {
      emit(statefulPluginSaved(payload));
    };

    return () => {
      options.eventHandler = null;
      options.navigationHandler = null;
      options.pluginSaveHandler = null;
    };
  });
}

/* eslint-enable no-param-reassign */

export function* readStatefulEvents(channel) {
  while (true) {
    const action = yield take(channel);
    yield put(action);
  }
}

export function* handleStatefulError() {
  while (true) {
    const { payload: { type, payload } } = yield take(STATEFUL_ERROR);

    if (type === 'socket-close') {
      // ignore
      return;
    }

    if (type === 'query-error') {
      const sessionUuid = yield select(selectSessionRouteParam);
      const toast = sessionUuid ? doradoSessionsViewQueryError
        : R.has('error', payload) && isSessionDuplicate(payload.error) ? sessionSaveDuplicateError
          : R.has('query', payload) && R.propEq('query', 'deleteSession', payload)
            ? null : doradoSessionsListQueryError;

      if (toast) {
        yield put(toast);
      }
    }

    /* eslint-disable-next-line no-console */
    console.error(payload);
  }
}

export function* initializeStateful() {
  const locale = yield select(selectLocale);
  const imsOrg = yield select(selectOrg);
  const imsToken = yield select(selectToken);

  const options = {
    endpoints: [],
    env: getEnv(),
    imsOrg,
    imsToken,
    locale,
    storageInterface: doradoReduxStorage
  };

  const statefulChannel = yield call(subscribeHandlers, options);
  const statefulTasks = [];

  statefulTasks.push(yield fork(readStatefulEvents, statefulChannel));
  statefulTasks.push(yield fork(handleStatefulError));

  const stateful = new Stateful(options);
  yield put(statefulLib.changeAction(stateful));
  yield put(statefulInited());

  yield call(cleanup, statefulChannel, statefulTasks);
}

export function* cleanup(channel, statefulTasks) {
  let inited = true;
  while (inited) {
    yield take(STATEFUL_RESET);

    channel.close();
    for (let i = 0; i < statefulTasks.length; i++) {
      const socketTask = statefulTasks[i];
      yield cancel(socketTask);
    }

    yield put(reset());
    inited = false;
  }
}

export default function* initializeStatefulSaga() {
  yield takeLatest(STATEFUL_INIT, initializeStateful);
}
